// src/codebase/components/RepoFormModal.jsx
//
// Create/edit a repository registration. Replaces the old RepoAddPage/
// RepoEditPage/RepoForm.jsx trio — same structure as admin/components/
// UserFormModal.jsx and Connections' own modals: behind the list page's
// header action, not a separate route.
//
// The source-control connection itself is never managed here — per
// explicit product direction, Codebase Analysis has no connections page
// of its own. The picker below reads from connectors/api/connectorsApi.js
// (the same API the Connections page itself uses, at /connectors) and
// links there when empty, instead of duplicating connection registration
// in a second place.
//
// source_control_connection_id and engagement_id are create-only: neither
// is accepted by RepoUpdateRequest (a repo's connection and engagement are
// fixed once registered), so both sections are hidden in edit mode.

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Modal, FormField, Input, Select, Button, Alert, SkeletonText, useDiscardConfirm } from "../../shared/ui";
import { listConnections } from "../../connectors/api/connectorsApi";

export default function RepoFormModal({ open, repo, engagementId, onClose, onSubmit }) {
  const isEdit = Boolean(repo);

  const [values, setValues] = useState({
    name: repo?.name || "",
    sourceControlConnectionId: "",
    defaultBranch: repo?.default_branch || "",
    description: repo?.description || "",
    sonarHostUrl: repo?.sonar_host_url || "",
    sonarProjectKey: repo?.sonar_project_key || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);
  const [dirty, setDirty] = useState(false);

  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(!isEdit);
  const [connectionsError, setConnectionsError] = useState(null);

  useEffect(() => {
    if (!open || isEdit) return;
    let cancelled = false;
    setConnectionsLoading(true);
    setConnectionsError(null);
    listConnections("source_control")
      .then((rows) => {
        if (!cancelled) setConnections(rows);
      })
      .catch((err) => {
        if (!cancelled) setConnectionsError(err?.message || "Failed to load connections.");
      })
      .finally(() => {
        if (!cancelled) setConnectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isEdit]);

  function handleClose() {
    setErrors({});
    setSaved(null);
    setSubmitting(false);
    setDirty(false);
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !saved, handleClose);

  function setField(name, v) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const nextErrors = {};
    if (!values.name.trim()) nextErrors.name = "Repository name is required.";
    if (!isEdit) {
      if (!values.sourceControlConnectionId) nextErrors.sourceControlConnectionId = "Select a connection.";
      if (!engagementId) nextErrors.sourceControlConnectionId = "Select an engagement from the switcher first.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload = isEdit
      ? {
          name: values.name.trim(),
          default_branch: values.defaultBranch.trim() || null,
          description: values.description.trim() || null,
          sonar_host_url: values.sonarHostUrl.trim() || null,
          sonar_project_key: values.sonarProjectKey.trim() || null,
        }
      : {
          name: values.name.trim(),
          source_control_connection_id: values.sourceControlConnectionId,
          engagement_id: engagementId,
          default_branch: values.defaultBranch.trim() || null,
          description: values.description.trim() || null,
          sonar_host_url: values.sonarHostUrl.trim() || null,
          sonar_project_key: values.sonarProjectKey.trim() || null,
        };

    setSubmitting(true);
    try {
      const result = await onSubmit(payload);
      setSaved(result);
    } catch (err) {
      if (err?.fieldErrors) setErrors(err.fieldErrors);
      else setErrors({ _form: err?.message || "Save failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  const connectionOptions = connections.map((c) => ({
    value: c.id,
    label: `${c.name} (${c.provider})`,
  }));

  return (
    <>
      <Modal
        open={open}
        size="lg"
        title={isEdit ? `Edit repository — ${repo.name}` : "Add repository"}
        onClose={requestClose}
      >
        {saved ? (
          <div className="flex flex-col gap-4">
            <Alert variant="success" icon={CheckCircle2} title={`"${saved.name}" ${isEdit ? "updated" : "registered"}`}>
              {isEdit
                ? "Changes apply to the next scan."
                : "Ready to scan — open it from the list to run the first scan."}
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            {errors._form && (
              <Alert variant="danger" icon={XCircle} title="Couldn't save">
                {errors._form}
              </Alert>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Repository Name" required error={Boolean(errors.name)} errorText={errors.name}>
                <Input
                  value={values.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g. BackendRepo1"
                  autoFocus
                />
              </FormField>

              <FormField label="Description" helperText="Optional.">
                <Input
                  value={values.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Short notes..."
                />
              </FormField>
            </div>

            {!isEdit && (
              <FormField
                label="Source-Control Connection"
                required
                error={Boolean(errors.sourceControlConnectionId)}
                errorText={errors.sourceControlConnectionId}
                helperText="Registered in Connections — carries the provider, repository, and credentials."
              >
                {connectionsLoading ? (
                  <div className="py-2 border border-line rounded-card px-3">
                    <SkeletonText lines={2} />
                  </div>
                ) : connectionsError ? (
                  <Alert variant="danger" icon={XCircle} title="Couldn't load connections">
                    {connectionsError}
                  </Alert>
                ) : connectionOptions.length === 0 ? (
                  <Alert variant="info" title="No source-control connections yet">
                    Register one in{" "}
                    <Link to="/connectors" className="underline font-semibold">
                      Connections
                    </Link>{" "}
                    first, then come back here.
                  </Alert>
                ) : (
                  <Select
                    options={[{ value: "", label: "Select a connection…" }, ...connectionOptions]}
                    value={values.sourceControlConnectionId}
                    onChange={(e) => setField("sourceControlConnectionId", e.target.value)}
                  />
                )}
              </FormField>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Default Branch"
                helperText="Branch to check out for scanning. Leave blank to use the repository's own default."
              >
                <Input
                  value={values.defaultBranch}
                  onChange={(e) => setField("defaultBranch", e.target.value)}
                  placeholder="main"
                />
              </FormField>

              <FormField label="Sonar Host URL" helperText="Optional.">
                <Input
                  value={values.sonarHostUrl}
                  onChange={(e) => setField("sonarHostUrl", e.target.value)}
                  placeholder="https://sonar.example.com"
                />
              </FormField>
            </div>

            <FormField label="Sonar Project Key" helperText="Optional.">
              <Input
                value={values.sonarProjectKey}
                onChange={(e) => setField("sonarProjectKey", e.target.value)}
                placeholder="my-project-key"
              />
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                disabled={!isEdit && (connectionsLoading || Boolean(connectionsError))}
              >
                {isEdit ? "Save changes" : "Add repository"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

RepoFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** null → create mode; a repo record → edit mode, seeded from it. */
  repo: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    default_branch: PropTypes.string,
    description: PropTypes.string,
    sonar_host_url: PropTypes.string,
    sonar_project_key: PropTypes.string,
  }),
  /** Required in create mode only — the workspace switcher's current engagement. */
  engagementId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
